import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { CustomDefinition } from '../../interfaces/db';
import { SessionListResponse } from '../../interfaces/sessions';
import { CustomMetricColumns, CustomDetailColumns, SESSION_METRICS_COLUMNS } from '../../interfaces/table';
import { Typography } from '../../interfaces/typography';
import { formatCustomScore } from '../../lib/redux/sessions/custom-scores/custom-score-formatter-hook';
import { getCustomCode } from './cfg';
import { convertFromReduxSafeCustomDefinitions } from '../../lib/redux/users/slice';

export interface CustomScoreDisplay {
  definition: CustomDefinition;
  value: number | null;
  displayValue: string;
  explanation: string | null;
  isPositive?: boolean;
}

export interface CustomScoresModalProps {
  session: SessionListResponse;
  isOpen: boolean;
  onClose: () => void;
  anchorElement?: HTMLElement | null;
  anchorRect?: DOMRect | null;
  position?: 'top' | 'bottom' | 'auto';
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

/**
 * Reusable modal component for displaying custom scores with hover interactions.
 * Can be used by both SessionList and SessionHistory components.
 */
export const CustomScoresModal: React.FC<CustomScoresModalProps> = ({
  session,
  isOpen,
  onClose,
  anchorElement,
  anchorRect,
  position = 'auto',
  onMouseEnter,
  onMouseLeave
}) => {
  const [portalNode] = useState(() => {
    if (typeof document !== 'undefined') {
      const node = document.createElement('div');
      node.style.position = 'fixed';
      node.style.left = '0';
      node.style.top = '0';
      node.style.width = '100%';
      node.style.height = '0';
      node.style.overflow = 'visible';
      node.style.zIndex = '9999';
      return node;
    }
    return null;
  });

  const modalRef = useRef<HTMLDivElement>(null);
  
  // Get custom definitions from Redux state
  const customDefinitions = useSelector((state: RootState) => state.customDefinitions.value);
  const hasCustomDefinitions = customDefinitions && customDefinitions.length > 0;

  // Handle portal mounting
  useEffect(() => {
    if (portalNode) {
      document.body.appendChild(portalNode);
      return () => {
        document.body.removeChild(portalNode);
      };
    }
  }, [portalNode]);

  // Handle clicks outside the modal
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  // Calculate modal position
  const getModalPosition = () => {
    const rect = anchorRect || (anchorElement ? anchorElement.getBoundingClientRect() : null);
    
    if (!rect) {
      return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }

    const modalWidth = 280;
    const modalHeight = 300;
    
    let top = rect.bottom - 2; // Slight overlap to ensure no gap
    let left = Math.max(8, Math.min(rect.left, window.innerWidth - modalWidth - 8));
    
    // Auto position based on available space
    if (position === 'auto') {
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      if (spaceBelow < modalHeight && spaceAbove > spaceBelow) {
        top = rect.top - modalHeight + 2; // Slight overlap
      }
    } else if (position === 'top') {
      top = rect.top - modalHeight + 2; // Slight overlap
    }

    return { top: `${top}px`, left: `${left}px` };
  };

  // Get team ID for custom codes
  const teamId = useSelector((state: RootState) => state.team.value?.team_id);
  const teamCustomCodes = React.useMemo(() => getCustomCode(teamId || ''), [teamId]);

  // Extract and format custom scores from session data
  const customScores = React.useMemo(() => {
    if (!session || !session.result) return [];
    if (!hasCustomDefinitions) return [];

    // Only process if session has live duration > 0 (connect/live person)
    const liveDuration = session.result.get(SESSION_METRICS_COLUMNS.LIVE_DURATION);
    if (!liveDuration || Number(liveDuration) <= 0) return [];

    const rawScores: Array<{
      definition: CustomDefinition;
      rawValue: any;
      explanation?: string;
    }> = [];
    
    // Get explanation data
    let explanationMap: Record<string, string> = {};
    try {
      const explanationData = session.result.get(CustomDetailColumns.METRIC_TO_EXPLANATION_JSON_STR);
      if (explanationData && typeof explanationData === 'string') {
        explanationMap = JSON.parse(explanationData);
      }
    } catch (e) {
      // Ignore parse errors
    }
    
    // Map custom definitions to metric values
    if (hasCustomDefinitions && customDefinitions) {
      const convertedDefinitions = convertFromReduxSafeCustomDefinitions(customDefinitions);
      convertedDefinitions.forEach((definition: CustomDefinition) => {
        if (definition.metric_idx !== null && definition.metric_idx >= 0 && definition.metric_idx <= 19) {
          const metricColumnKey = `METRIC_${definition.metric_idx}` as keyof typeof CustomMetricColumns;
          const metricColumn = CustomMetricColumns[metricColumnKey];
          
          if (metricColumn) {
            const rawValue = session.result.get(metricColumn);
            const explanation = explanationMap[definition.metric_idx.toString()] || undefined;
            
            rawScores.push({
              definition,
              rawValue,
              explanation
            });
          }
        }
      });
    }
    
    
    // Use the formatter to get consistent formatting
    return rawScores
      .map(score => formatCustomScore({
        session,
        definition: score.definition,
        rawValue: score.rawValue,
        explanation: score.explanation,
        teamCustomCodes
      }))
      .filter((score): score is NonNullable<typeof score> => score !== null);
  }, [session, customDefinitions, hasCustomDefinitions, teamCustomCodes]);

  if (!isOpen || !portalNode || customScores.length === 0) {
    return null;
  }

  const modalPosition = getModalPosition();

  const modalContent = (
    <div 
      ref={modalRef}
      className="fixed bg-white rounded-lg shadow-xl border border-gray-200 z-[9999] w-[280px] max-h-[300px] overflow-y-auto"
      style={modalPosition}
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={(e) => {
        e.stopPropagation();
        onMouseEnter?.();
      }}
      onMouseLeave={(e) => {
        e.stopPropagation();
        onMouseLeave?.();
      }}
    >
      <div className="sticky top-0 bg-white border-b border-gray-100 px-3 py-2 flex justify-between items-center">
        <Typography variant="mediumParagraph" fontWeight="600" color="#111827">
          Custom Scores ({customScores.length})
        </Typography>
        <button 
          onClick={onClose}
          className="p-0.5 hover:bg-gray-100 rounded-full transition-colors"
        >
          <X size={14} className="text-gray-500" />
        </button>
      </div>
      
      <div className="p-3">
        <div className="space-y-2">
          {customScores.map((score, index) => (
            <div 
              key={index}
              className="border border-gray-100 rounded overflow-hidden hover:border-gray-200 transition-colors"
            >
              <div className="p-2">
                <div className="flex items-center justify-between mb-1">
                  <Typography variant="mediumParagraph" fontWeight="600" color="#111827">
                    {score.label}
                  </Typography>
                  <span 
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: score.backgroundColor,
                      color: score.textColor
                    }}
                  >
                    {score.displayValue}
                  </span>
                </div>
                
                {score.explanation && (
                  <Typography variant="smallParagraph" color="#6b7280">
                    {score.explanation}
                  </Typography>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, portalNode);
};